'use client';
import React from 'react';
import { Menu, RefreshCw, TrendingUp } from 'lucide-react';
import { useModel } from '@/context/ModelContext';
import { fmtCrShort, fmtPct } from '@/lib/financial-model';

interface TopbarProps {
  onMobileMenuToggle: () => void;
}

export default function Topbar({ onMobileMenuToggle }: TopbarProps) {
  const { model, assumptions, showCrores, toggleDisplayMode, resetToDefaults } = useModel();
  const yr2 = model.years[2];
  const projectIrrPct = (model.projectIRR * 100).toFixed(1);
  const [copyLinkText, setCopyLinkText] = React.useState('Share Link');

  const handleShareLink = () => {
    const base64 = btoa(JSON.stringify(assumptions));
    const url = new URL(window.location.href);
    url.searchParams.set('state', base64);
    navigator.clipboard.writeText(url.toString());
    setCopyLinkText('Copied!');
    setTimeout(() => setCopyLinkText('Share Link'), 2000);
  };

  const metrics = [
    { label: 'PROJECT IRR', value: `${projectIrrPct}%`, highlight: true },
    { label: 'NPV @14%', value: fmtCrShort(model.projectNPV), highlight: false },
    { label: 'YR2 PAT', value: yr2 ? fmtCrShort(yr2.pat) : '—', highlight: false },
    { label: 'EBITDA%', value: yr2 ? fmtPct(yr2.ebitdaMarginPct) : '—', highlight: false },
    {
      label: 'PAYBACK',
      value:
        model.paybackYearFractional > 0 ? `Yr ${model.paybackYearFractional.toFixed(1)}` : 'N/A',
      highlight: false,
    },
  ];

  return (
    <header
      className="h-12 flex items-center px-4 gap-3 flex-shrink-0 z-20"
      style={{
        background: 'var(--surface-1)',
        borderBottom: '1px solid var(--divider)',
      }}
    >
      {/* Mobile menu toggle */}
      <button
        onClick={onMobileMenuToggle}
        className="lg:hidden p-1.5 rounded transition-colors"
        style={{ color: 'var(--text-tertiary)' }}
        aria-label="Toggle mobile menu"
      >
        <Menu size={18} />
      </button>

      {/* Metrics strip */}
      <div className="hidden md:flex items-center gap-0 flex-1 overflow-x-auto scrollbar-thin">
        {metrics.map((m, i) => (
          <React.Fragment key={`metric-${m.label}`}>
            {i > 0 && (
              <div
                className="w-px h-5 mx-3 flex-shrink-0"
                style={{ background: 'var(--divider)' }}
              />
            )}
            <div className="flex items-center gap-2 flex-shrink-0">
              <span
                style={{
                  fontSize: '0.6rem',
                  fontWeight: 700,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: 'var(--text-tertiary)',
                }}
              >
                {m.label}
              </span>
              <span
                className="font-mono"
                style={{
                  fontSize: '0.8125rem',
                  fontWeight: 700,
                  color: m.highlight ? 'var(--gold)' : 'var(--text-primary)',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {m.value}
              </span>
            </div>
          </React.Fragment>
        ))}
      </div>

      {/* Right actions */}
      <div className="flex items-center gap-2 ml-auto">
        <button
          onClick={toggleDisplayMode}
          className="btn-secondary hidden sm:flex"
          title="Toggle between Crores and absolute ₹ display"
        >
          {showCrores ? '₹ CR' : '₹ ABS'}
        </button>
        <button
          onClick={resetToDefaults}
          className="btn-secondary flex items-center gap-1.5"
          title="Reset all assumptions to default base case values"
        >
          <RefreshCw size={11} />
          <span className="hidden sm:inline">Reset</span>
        </button>

        <button 
          onClick={handleShareLink}
          className="btn-secondary flex items-center gap-1.5"
          title="Copy a shareable link"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"></path><polyline points="16 6 12 2 8 6"></polyline><line x1="12" y1="2" x2="12" y2="15"></line></svg>
          <span className="hidden sm:inline">{copyLinkText}</span>
        </button>

        <button 
          onClick={() => window.print()}
          className="btn-secondary flex items-center gap-1.5"
          title="Download PDF Report"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
          <span className="hidden sm:inline">PDF</span>
        </button>
        <div
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded"
          style={{
            background: 'rgba(201,168,76,0.1)',
            border: '1px solid rgba(201,168,76,0.25)',
          }}
        >
          <TrendingUp size={11} style={{ color: 'var(--gold)' }} />
          <span
            style={{
              fontSize: '0.6rem',
              fontWeight: 700,
              letterSpacing: '0.1em',
              color: 'var(--gold)',
              textTransform: 'uppercase',
            }}
          >
            GO WITH CONDITIONS
          </span>
        </div>
      </div>
    </header>
  );
}
